import { useEffect, useState } from "react";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { Bar, ComposedChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis, Legend, CartesianGrid } from "recharts";
import { Loader2 } from "lucide-react";
import api from "@/lib/api";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const label = (k) => `${MONTHS[Number(k.slice(5, 7)) - 1]} ${k.slice(2, 4)}`;
const money = (cur, v) => `${cur}${Math.round(v || 0).toLocaleString()}`;
const kfmt = (v) => (Math.abs(v) >= 1e6 ? `${(v / 1e6).toFixed(1)}m` : `${Math.round(v / 1000)}k`);

const SOURCE_NOTE = {
  manual_sheet: "from Loan Costing sheet",
  airtable_broker_invoices: "from Airtable Broker Invoices",
};

// Generic "amount funded vs <metric>" 6-month hover chart with a % line.
export function MonthlyMetricHover({ endpoint, metricKey, metricLabel, title, color, region, curSym, testId, children }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!open || data) return;
    api.get(`${endpoint}?region=${region}&months=6`)
      .then(r => setData(r.data)).catch(() => setData({ months: [], error: true }));
  }, [open, data, region, endpoint]);
  useEffect(() => { setData(null); }, [region]);
  const rows = (data?.months || []).map(m => ({ ...m, name: label(m.month) }));
  return (
    <HoverCard openDelay={150} onOpenChange={setOpen}>
      <HoverCardTrigger asChild>{children}</HoverCardTrigger>
      <HoverCardContent align="end" className="w-[560px] p-3" data-testid={testId}>
        <div className="flex items-baseline justify-between mb-1">
          <div className="text-sm font-semibold">{title}</div>
          <div className="text-[10px] text-muted-foreground">{region === "AUS" ? "AU" : "UK"} · % = {metricLabel.toLowerCase()} ÷ funded</div>
        </div>
        {!data ? (
          <div className="h-56 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
        ) : rows.length === 0 ? (
          <div className="h-56 flex items-center justify-center text-xs text-muted-foreground">{data.error ? "Couldn't load the data" : "No data yet"}</div>
        ) : (
          <ResponsiveContainer width="100%" height={230}>
            <ComposedChart data={rows} margin={{ top: 8, right: 8, left: 0, bottom: 0 }} barGap={2}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis yAxisId="amt" tick={{ fontSize: 10 }} tickFormatter={kfmt} width={44} />
              <YAxis yAxisId="pct" orientation="right" domain={[0, "auto"]} tick={{ fontSize: 10 }} tickFormatter={(v) => `${v}%`} width={36} />
              <Tooltip
                formatter={(v, name) => name === "% of funded" ? [`${v == null ? "—" : v.toFixed(1)}%`, name] : [money(curSym, v), name]}
                labelFormatter={(l, p) => {
                  const m = p?.[0]?.payload;
                  const note = SOURCE_NOTE[m?.source];
                  const deals = m?.deals != null ? ` · ${m.processed != null ? `${m.processed}/` : ""}${m.deals} deals` : "";
                  return `${l}${note ? ` · ${note}` : ""}${deals}`;
                }}
                contentStyle={{ fontSize: 11 }}
              />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar yAxisId="amt" dataKey="funded" name="Amount funded" fill="#3B70E0" radius={[3, 3, 0, 0]} />
              <Bar yAxisId="amt" dataKey={metricKey} name={metricLabel} fill={color} radius={[3, 3, 0, 0]} />
              <Line yAxisId="pct" type="monotone" dataKey="pct" name="% of funded" stroke="#b45309" strokeWidth={2} dot={{ r: 3 }} connectNulls />
            </ComposedChart>
          </ResponsiveContainer>
        )}
        {rows.length > 0 && (
          <div className="grid grid-cols-6 gap-1 mt-1 text-[10px] text-center" data-testid={`${testId}-table`}>
            {rows.map(m => (
              <div key={m.month} className="rounded border bg-slate-50 px-1 py-0.5">
                <div className="font-semibold">{m.name}</div>
                <div className="text-slate-600">{money(curSym, m[metricKey])}</div>
                <div className="text-amber-800 font-medium">{m.pct == null ? "—" : `${m.pct.toFixed(1)}%`}</div>
              </div>
            ))}
          </div>
        )}
      </HoverCardContent>
    </HoverCard>
  );
}

export const CostOfFundingHover = (props) => (
  <MonthlyMetricHover endpoint="/applications/funded/cost-of-funding/monthly" metricKey="cost_of_funding" metricLabel="Cost of funding"
    title="Amount funded vs cost of funding — last 6 months" color="#0ea5e9" testId="cost-of-funding-hover" {...props} />
);

export const CostOfSalesHover = (props) => (
  <MonthlyMetricHover endpoint="/applications/funded/cost-of-sales/monthly" metricKey="cost_of_sales" metricLabel="Cost of sales"
    title="Amount funded vs cost of sales (broker comms) — last 6 months" color="#d97706" testId="cost-of-sales-hover" {...props} />
);
